import moment from 'moment-timezone';

export const CHART_DAYS = 10;

const DAY_KEY_FORMAT = 'YYYY-MM-DD';

// Stats key charted for a goal type; any goal type not listed uses its own name.
const GOAL_METRIC: Record<string, string> = {
  budget: 'total_spend',
  store_visits: 'footfall_attribution'
};

// Same icons as the reporting Summary boxes.
const GOAL_ICON: Record<string, string> = {
  audio_complete: 'fal fa-headphones-alt',
  audio_start: 'fal fa-headphones-alt',
  budget: 'fal fa-coins',
  clicks: 'fa-regular fa-hand-back-point-up',
  engagements: 'fal fa-handshake',
  impressions: 'fak fa-impressions',
  pages_viewed: 'fal fa-globe-pointer',
  playouts: 'fa-kit fa-screen-play',
  store_visits: 'fal fa-glasses-alt',
  video_complete: 'fal fa-video',
  video_start: 'fal fa-video'
};

// Default copy for each goal type; translations live in Tolgee.
export const GOAL_LABEL: Record<string, string> = {
  audio_complete: 'Audio Completes',
  audio_start: 'Audio Starts',
  clicks: 'Clicks',
  engagements: 'Engagements',
  impressions: 'Impressions',
  pages_viewed: 'Pages Viewed',
  playouts: 'Playouts',
  store_visits: 'Store Visits',
  video_complete: 'Video Completes',
  video_start: 'Video Starts'
};

export function getGoalMetric(goalType: string): string {
  return GOAL_METRIC[goalType] ?? goalType;
}

export function getGoalIcon(goalType: string): string {
  return GOAL_ICON[goalType] ?? 'fal fa-chart-line';
}

// Budget is always fetched; any other overall goal adds its own metric.
export function getQuickReportMetrics(goals: { timeFrame: string; type: string }[] = []): string[] {
  const overallGoalMetrics = goals.filter(goal => goal.timeFrame === 'overall').map(goal => getGoalMetric(goal.type));
  return [...new Set(['total_spend', ...overallGoalMetrics])];
}

// The CHART_DAYS calendar days ending on lastDay, oldest first.
export function getChartDays(lastDay: moment.Moment): moment.Moment[] {
  return Array.from({ length: CHART_DAYS }, (_, index) => lastDay.clone().subtract(CHART_DAYS - 1 - index, 'days'));
}

// One value per calendar day; days without stats count as zero.
export function buildChartValues(
  days: moment.Moment[],
  stats: Record<string, unknown>[] | undefined,
  metricKey: string
): number[] {
  const valueByDay = new Map(
    (stats ?? []).map(entry => [moment.utc(entry.day as string).format(DAY_KEY_FORMAT), Number(entry[metricKey] ?? 0)])
  );
  return days.map(day => valueByDay.get(day.format(DAY_KEY_FORMAT)) ?? 0);
}

// Negative Y floor that lifts zero to bottomRatio of the plot height, leaving a band below it.
export function getYAxisFloor(values: number[], bottomRatio = 0.35): number {
  const max = Math.max(...values, 0);
  return -((max * bottomRatio) / (1 - bottomRatio));
}

export type EntityWindow = { begin: moment.Moment; end: moment.Moment };

export type TooltipPoint = {
  cumulative: number;
  day: moment.Moment;
  dayNumber?: number;
  dayValue: number;
  progressPct: number;
  totalDays?: number;
};

// Per chart day: the day value, the goal total reached that day (current minus the later days), its progress
// over the target and its position within the entity window.
export function buildTooltipPoints(
  days: moment.Moment[],
  values: number[],
  current: number,
  target: number,
  window?: EntityWindow
): TooltipPoint[] {
  return values.map((dayValue, index) => {
    const laterDaysValue = values.slice(index + 1).reduce((acc, value) => acc + value, 0);
    const cumulative = Math.max(current - laterDaysValue, 0);
    const point = { cumulative, day: days[index], dayValue, progressPct: target ? (cumulative / target) * 100 : 0 };
    if (!window) {
      return point;
    }
    const totalDays = window.end.diff(window.begin, 'days') + 1;
    const dayNumber = days[index].diff(window.begin, 'days') + 1;
    return dayNumber < 1 || dayNumber > totalDays ? point : { ...point, dayNumber, totalDays };
  });
}
